import { PropsWithChildren, useCallback } from "react";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { Cross1Icon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NumericFilterMenuItemType,
  numericOperatorToLabel,
} from "../FilterMenu.model";
import {
  GreaterThanOrEqualFilter,
  LessThanOrEqualFilter,
  numericFilter,
  NumericOperator,
} from "../FieldFilter.model";
import { NumericBoundInput } from "../NumericBoundInput";
import styles from "../FilterMenu.module.scss";

interface AmountMenuProps {
  label: string;
  value: number;
  onSubmit: (value: number) => void;
  disabled?: boolean;
}

function AmountMenu({
  children,
  label,
  value,
  onSubmit,
  disabled = false,
}: PropsWithChildren<AmountMenuProps>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger disabled={disabled} asChild>{children}</DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[240px]">
        <NumericBoundInput label={label} value={value} onSubmit={onSubmit} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface NumericActiveFilterItemProps<Field extends string>
  extends NumericFilterMenuItemType<Field> {
  value: GreaterThanOrEqualFilter | LessThanOrEqualFilter;
  size: "1" | "2" | "3";
  viewOnly: boolean;
  onChange: (item: NumericFilterMenuItemType<Field>) => void;
}

export function NumericActiveFilterItem<Field extends string>({
  size,
  viewOnly,
  onChange,
  ...item
}: NumericActiveFilterItemProps<Field>) {
  const amount = item.value.value;

  const handleOperatorChange = useCallback(
    (operator: NumericOperator) =>
      onChange({ ...item, value: numericFilter(operator, item.value.value) }),
    [item, onChange]
  );

  const handleAmountChange = useCallback(
    (value: number) =>
      onChange({ ...item, value: numericFilter(item.value.operator, value) }),
    [item, onChange]
  );

  const handleClear = useCallback(
    () => onChange({ ...item, value: undefined }),
    [item, onChange]
  );

  return (
    <Flex gap={"1px"} className={styles.activeFilter} align="center">
      <div
        data-accent-color="gray"
        className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
      >
        {item.icon}
        {item.label}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            data-accent-color="gray"
            className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
              viewOnly ? "pointer-events-none" : ""
            }`}
          >
            {numericOperatorToLabel[item.value.operator]}
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start">
          <DropdownMenuCheckboxItem
            checked={item.value.operator === "gte"}
            onCheckedChange={() => handleOperatorChange("gte")}
          >
            {numericOperatorToLabel.gte}
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={item.value.operator === "lte"}
            onCheckedChange={() => handleOperatorChange("lte")}
          >
            {numericOperatorToLabel.lte}
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AmountMenu
        label={item.label}
        value={amount}
        onSubmit={handleAmountChange}
        disabled={viewOnly}
      >
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
            viewOnly ? "pointer-events-none" : ""
          } shrink`}
        >
          <Text truncate>{amount}</Text>
        </div>
      </AmountMenu>

      {!viewOnly && (
        <IconButton variant="soft" color="gray" size={size} onClick={handleClear}>
          <Cross1Icon />
        </IconButton>
      )}
    </Flex>
  );
}
